/**
 * @fileoverview Owner-only QR voucher detail — re-fetches the labeled SVG so
 * the owner can view, print, or share it again after navigating away from the
 * create flow. Also surfaces revoke/delete (with claimed-warning).
 */

import React, { useEffect, useState } from 'react';
import { ScrollView, View, Text, StyleSheet, Alert, TouchableOpacity } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { useOwnerScope } from '../../../../src/context/OwnerScopeContext';
import Card from '../../../../src/components/Card';
import Button from '../../../../src/components/Button';
import ContentContainer from '../../../../src/components/ContentContainer';
import QrSvgView from '../../../../src/components/QrSvgView';
import { useToast } from '../../../../src/components/Toast';
import { useTheme } from '../../../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, type ColorTheme } from '../../../../src/utils/theme';

interface VoucherDetail {
  id: string;
  code: string;
  kind: 'gift' | 'promo' | 'points_redemption';
  discountKind: 'percent' | 'free_wash' | 'free_minutes' | 'free_vend' | 'credit' | 'free_upsell';
  percentOff: number | null;
  tierName: string | null;
  freeMinutes?: number | null;
  /** Not returned by by-code today; label falls back to a generic string. */
  creditCents?: number | null;
  /** Human label for multi-select applicability rows; null on legacy rows. */
  appliesToLabel?: string | null;
  redemptionLimit: number;
  redemptionsUsed: number;
  perCustomerLimit: number;
  status: 'pending' | 'active' | 'redeemed' | 'expired' | 'revoked';
  expiresAt: string | null;
  source: string;
}

export default function QrDetailScreen({ entityId }: { entityId?: string } = {}) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const scope = useOwnerScope();
  const toast = useToast();
  const params = useLocalSearchParams<{ id: string }>();
  const id = entityId ?? params.id;

  const [voucher, setVoucher] = useState<VoucherDetail | null>(null);
  const [svg, setSvg] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [usedCount, setUsedCount] = useState<number | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    // Supplemental stat — "used at the wash" (customers with ≥1 actual redemption).
    // redemptionsUsed counts claim-reserved slots for QR vouchers, not washes.
    // Silent-fail: the row just shows "—" if this doesn't load.
    scope.api.get<{ summary?: { usedCount?: number } }>(`/api/vouchers/${id}/claims?page=1&limit=1`)
      .then(d => { if (!cancelled && typeof d.summary?.usedCount === 'number') setUsedCount(d.summary.usedCount); })
      .catch(() => { /* supplemental; keep silent */ });
    (async () => {
      try {
        // Fetch QR SVG (the only endpoint that returns the labeled image)
        const qr = await scope.api.get<{ svg: string; redemptionUrl: string; code: string }>(`/api/vouchers/${id}/qr`);
        if (cancelled) return;
        setSvg(qr.svg);
        setUrl(qr.redemptionUrl);
        // Look up voucher metadata via the public by-code endpoint
        const meta = await scope.api.get<VoucherDetail>(`/api/vouchers/by-code/${qr.code}`);
        if (cancelled) return;
        setVoucher({ ...meta, id });
      } catch (err: any) {
        if (!cancelled) toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const handleDelete = () => {
    if (!voucher) return;
    if (voucher.redemptionsUsed > 0) {
      Alert.alert(
        'QR already used',
        `This QR has been redeemed ${voucher.redemptionsUsed} time(s). You can no longer delete it.`,
        [{ text: 'OK' }],
      );
      return;
    }
    Alert.alert(
      'Delete this QR?',
      'The redemption code will stop working immediately. This cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete', style: 'destructive', onPress: async () => {
            try {
              await scope.api.api(`/api/vouchers/${voucher.id}`, { method: 'DELETE' });
              toast.show({ type: 'success', title: 'Deleted' });
              router.back();
            } catch (err: any) {
              toast.show({ type: 'error', title: 'Error', message: err.message });
            }
          },
        },
      ],
    );
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer>
        <Card style={styles.card}>
          {svg ? (
            <View style={{ alignItems: 'center', marginVertical: spacing.md }}>
              <QrSvgView uri={svg} width={320} accessibilityLabel="QR code" />
            </View>
          ) : (
            <Text style={styles.hint}>Loading…</Text>
          )}

          {voucher && (
            <>
              <View style={styles.row}>
                <Text style={styles.label}>Code</Text>
                <Text style={styles.value}>{voucher.code}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Offer</Text>
                <Text style={[styles.value, { flexShrink: 1, textAlign: 'right' }]}>
                  {voucher.discountKind === 'credit'
                    // by-code omits creditCents; credit is scope-free by design.
                    ? (voucher.creditCents != null ? `$${(voucher.creditCents / 100).toFixed(2)} gift credit` : 'Gift credit (deposited at claim)')
                    : voucher.discountKind === 'free_upsell'
                    ? (voucher.appliesToLabel ? `Free add-on with a wash · ${voucher.appliesToLabel}` : 'Free add-on with a wash')
                    : voucher.discountKind === 'free_vend'
                    // by-code omits freeVendCents; appliesToLabel still names Vending.
                    ? (voucher.appliesToLabel ? `Vending credit · ${voucher.appliesToLabel}` : 'Vending credit')
                    : voucher.discountKind === 'free_minutes'
                      ? (voucher.appliesToLabel
                        ? `${voucher.freeMinutes ?? '?'} free minutes · ${voucher.appliesToLabel}`
                        : `${voucher.freeMinutes ?? '?'} free minutes`)
                      : voucher.discountKind === 'free_wash' || (voucher.discountKind === 'percent' && voucher.percentOff === 100)
                        ? (voucher.appliesToLabel ? `Free wash · ${voucher.appliesToLabel}` : `Free ${voucher.tierName || 'wash'}`)
                        : (voucher.appliesToLabel
                          ? `${voucher.percentOff}% off · ${voucher.appliesToLabel}`
                          : `${voucher.percentOff}% off${voucher.tierName ? ` ${voucher.tierName}` : ''}`)}
                </Text>
              </View>
              <TouchableOpacity
                style={styles.row}
                onPress={() => router.push(`${scope.basePath}/subscriptions/vouchers/${id}`)}
              >
                <Text style={styles.label}>Redemptions</Text>
                <View style={styles.linkValue}>
                  <Text style={styles.value}>{voucher.redemptionsUsed} / {voucher.redemptionLimit}</Text>
                  <Ionicons name="chevron-forward" size={14} color={colors.primary} />
                </View>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.row}
                onPress={() => router.push(`${scope.basePath}/subscriptions/vouchers/${id}`)}
              >
                <Text style={styles.label}>Used at the wash</Text>
                <View style={styles.linkValue}>
                  <Text style={styles.value}>{usedCount ?? '—'}</Text>
                  <Ionicons name="chevron-forward" size={14} color={colors.primary} />
                </View>
              </TouchableOpacity>
              <View style={styles.row}>
                <Text style={styles.label}>Per customer</Text>
                <Text style={styles.value}>{voucher.perCustomerLimit}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Status</Text>
                <Text style={[styles.value, { color: voucher.status === 'active' ? colors.success : colors.textSecondary }]}>{voucher.status}</Text>
              </View>
              {voucher.expiresAt && (
                <View style={styles.row}>
                  <Text style={styles.label}>Expires</Text>
                  <Text style={styles.value}>{new Date(voucher.expiresAt).toLocaleDateString()}</Text>
                </View>
              )}
              {url && (
                <View style={[styles.row, { borderBottomWidth: 0 }]}>
                  <Text style={styles.label}>Link</Text>
                  <Text style={[styles.value, { flexShrink: 1, fontSize: fontSize.xs }]} numberOfLines={2}>{url}</Text>
                </View>
              )}
            </>
          )}
        </Card>

        {voucher && (voucher.status === 'active' || voucher.status === 'pending') && (
          <Button
            title="Delete QR"
            icon={<Ionicons name="trash" size={20} color={colors.white} />}
            variant="danger"
            onPress={handleDelete}
            style={{ marginTop: spacing.md }}
          />
        )}
        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  card: { padding: spacing.lg },
  row: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  label: { color: c.textSecondary, fontSize: fontSize.sm },
  value: { color: c.text, fontSize: fontSize.sm, fontWeight: fontWeight.semibold as any },
  linkValue: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  hint: { color: c.textLight, fontStyle: 'italic', textAlign: 'center', padding: spacing.lg },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
